// What the document reader found on an application, put into words a manager
// can act on during the interview.
//
// The verdicts come from applicant_screening (migration 0262g), surfaced on
// v_driver_applications_admin by 0262n. The reader looks at the PrDP photo and
// the Uber profile screenshot the Driver Employment Form asks for. A null
// verdict means the application was never read (it predates the reader, or it
// is still in the queue), and that is shown as "not checked", never as a pass.
import type { ChipTone } from '../components/interviews/Chip'

export type ScreeningFields = {
  prdp_verdict?: string | null
  prdp_expiry?: string | null
  prdp_categories?: string | null
  uber_verdict?: string | null
  uber_evidence?: string | null
  screening_read_failure?: string | null
  // Migration 0331. 'card' or 'temporary' (the paper TDL(5) form); null when
  // the application was read before the reader knew the difference.
  prdp_document_type?: string | null
  // The last day the PrDP on file is valid: for a temporary form, the earlier
  // of the PrDP expiry and six months from its issue date.
  prdp_valid_until?: string | null
  prdp_id_check?: string | null
  prdp_card_received_at?: string | null
}

export type ScreeningLine = {
  tone: ChipTone
  label: string
  // What the manager should do about it, or null when nothing is needed.
  action: string | null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// A Postgres date arrives as 'YYYY-MM-DD' (or a full timestamp). Read the date
// part only, so the time zone of the browser can never move it by a day.
export function formatExpiry(value: string | null | undefined): string | null {
  if (!value) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
  if (!m) return null
  const month = MONTHS[Number(m[2]) - 1]
  if (!month) return null
  return `${Number(m[3])} ${month} ${m[1]}`
}

export function prdpLine(f: ScreeningFields): ScreeningLine {
  const exp = formatExpiry(f.prdp_expiry)
  const cats = f.prdp_categories ? ` (${f.prdp_categories})` : ''
  switch (f.prdp_verdict) {
    case 'valid': {
      if (f.prdp_document_type === 'temporary' && !f.prdp_card_received_at) {
        const until = formatExpiry(f.prdp_valid_until)
        return {
          tone: 'green',
          label: `Temporary PrDP${cats}, valid until ${until ?? 'date not read'}`,
          action: until
            ? `This is the paper temporary licence, not the card. Ask for the PrDP card; it has to be seen before ${until}.`
            : 'This is the paper temporary licence, not the card. The issue date was not read, so check it on the form.',
        }
      }
      if (f.prdp_document_type === 'temporary') {
        return { tone: 'green', label: `PrDP card seen${cats}${exp ? `, expires ${exp}` : ''}`, action: null }
      }
      return { tone: 'green', label: `PrDP valid${cats}${exp ? `, expires ${exp}` : ''}`, action: null }
    }
    case 'temporary_lapsed': {
      const until = formatExpiry(f.prdp_valid_until)
      return {
        tone: 'amber',
        label: `Temporary PrDP form lapsed${until ? ` ${until}` : ''}${cats}`,
        action: `The paper form is valid for six months only. The PrDP itself${exp ? ` runs to ${exp}` : ' may still be current'}: ask for the PrDP card.`,
      }
    }
    case 'expired':
      return {
        tone: 'amber',
        label: `PrDP expired${exp ? ` ${exp}` : ''}`,
        action: 'A renewal away from a job. Ask when they will renew; they cannot drive until it is renewed.',
      }
    case 'no_passenger_category':
      return {
        tone: 'red',
        label: `PrDP has no P category${cats}`,
        action: 'Cannot drive passengers on this permit. Ask whether they hold a P category PrDP.',
      }
    case 'not_prdp':
      return {
        tone: 'red',
        label: 'Upload is not a PrDP',
        action: 'Ask them to show the back of their licence card, where the PrDP is printed.',
      }
    case 'not_uploaded':
      return { tone: 'red', label: 'No PrDP uploaded', action: 'Ask whether they hold a PrDP and to send a photo.' }
    case 'unclear':
      if (f.screening_read_failure) {
        return { tone: 'amber', label: 'PrDP not checked: the reader failed', action: 'Check the PrDP photo yourself.' }
      }
      return {
        tone: 'amber',
        label: 'PrDP photo not readable',
        action: 'Ask for a clear photo of the back of the licence card and check the P category and expiry.',
      }
    default:
      return { tone: 'neutral', label: 'PrDP not checked', action: 'Check the PrDP yourself before any offer.' }
  }
}

// Only said when there is something wrong. The ID number on the PrDP is
// compared with the application in Postgres (prdp_assess_v1); a mismatch holds
// the application for a person and is never passed automatically.
export function idLine(f: ScreeningFields): ScreeningLine | null {
  if (f.prdp_id_check === 'near_match') {
    return {
      tone: 'amber',
      label: 'PrDP ID number differs by one digit',
      action: 'Probably a misread, but compare the ID number on the PrDP with their ID document.',
    }
  }
  if (f.prdp_id_check !== 'mismatch') return null
  return {
    tone: 'red',
    label: 'PrDP ID number does not match the application',
    action: 'Check that the upload belongs to this applicant before going any further.',
  }
}

export function uberLine(f: ScreeningFields): ScreeningLine {
  switch (f.uber_verdict) {
    case 'ok':
      return { tone: 'green', label: 'Uber profile in good standing', action: null }
    case 'no_profile':
      return { tone: 'neutral', label: 'No Uber profile', action: 'Fleetly helps with fingerprints and the driver evaluation.' }
    case 'blocked':
      return { tone: 'red', label: 'Uber account deactivated', action: 'Do not proceed without speaking to Luyanda.' }
    case 'review':
      return { tone: 'amber', label: 'Uber account on hold', action: 'Ask why the account is on hold.' }
    case 'not_uploaded':
      return { tone: 'amber', label: 'No Uber screenshot', action: 'Ask them to show their Uber driver profile.' }
    case 'unclear':
      return { tone: 'amber', label: 'Uber screenshot not readable', action: 'Ask them to show their Uber driver profile.' }
    default:
      return { tone: 'neutral', label: 'Uber profile not checked', action: null }
  }
}

// Why an application is where it is, from the reason written with its last
// status change. The automatic rules write short codes; a manager writes a
// sentence. A code is put into words, a sentence is shown as written.
const REASON_WORDS: Record<string, string> = {
  prdp_expired: 'Closed by the system: the PrDP has expired',
  prdp_no_passenger_category: 'Closed by the system: the PrDP has no P (passenger) category',
  prdp_not_uploaded: 'Closed by the system: no PrDP was uploaded',
  prdp_not_prdp: 'Closed by the system: the upload was not a PrDP',
  prdp_unreadable_unclear: 'Closed by the system: the PrDP photo could not be read. Asked to apply again with a clear photo',
  prdp_unreadable_not_prdp: 'Closed by the system: the upload was not a PrDP. Asked to apply again with the right document',
  uber_blocked: 'Closed by the system: the Uber account is deactivated',
  not_gauteng: 'Closed by the system: does not live in Gauteng',
  outside_gauteng: 'Closed by the system: does not live in Gauteng',
  identity_blocked: 'Closed by the system: this person may not be re-employed',
  screening_passed: 'Passed the document check',
  screening_passed_flagged: 'Passed the document check, with a flag for the interview',
  prdp_corrected: 'Reopened: the document check misread the PrDP or applied the wrong rule',
  prdp_id_mismatch_review: 'Held: the ID number on the PrDP does not match the application. Check it by hand',
}

export function statusReasonText(reason: string | null | undefined): string | null {
  const r = (reason ?? '').trim()
  if (!r) return null
  if (REASON_WORDS[r]) return REASON_WORDS[r]!
  if (/^[a-z0-9_]+$/.test(r)) {
    const words = r.replace(/_/g, ' ')
    return words.charAt(0).toUpperCase() + words.slice(1)
  }
  return r
}
